// §4.12 Planning parameters (`/planning/settings/parameters`): the products
// planned at the facility, a drawer that edits one product's parameters and its
// safety-stock policy, and a bar that applies one field to many rows by a
// client fan-out with an outcome per row (D-323 ruling 7's pattern).

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { editGridQty, formatGridQty, parseGridQty } from "../../../../components/bucket-grid-paste";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { ProductPicker } from "../components/product-picker";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  problemCode,
  problemExtra,
  putPlanningParameters,
  putSafetyStock,
  readParametersOf,
  usePlanningParameters,
  usePlanningRefresh,
  useProductIndex,
  type Facility,
  type ParameterFilters,
  type PlanningParameters,
  type PlanningParametersPut,
  type PlanningParametersRow,
  type Product,
  type SafetyStockPolicy,
  type SafetyStockPut,
} from "../hooks/planning-queries";
import { parseWhole, useOffline } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { shortId } from "../../../../lib/short-id";

/** The PUT body that writes `row` back as it is, with `patch` laid over it. */
export function parametersBody(
  row: PlanningParameters,
  patch: Partial<PlanningParametersPut> = {},
): PlanningParametersPut {
  return {
    replenishment_type: row.replenishment_type,
    source_facility_id: row.source_facility_id,
    mps_relevant: row.mps_relevant,
    lead_time_days: row.lead_time_days,
    lot_sizing_method: row.lot_sizing_method,
    min_lot_qty: row.min_lot_qty,
    lot_multiple_qty: row.lot_multiple_qty,
    frozen_fence_days: row.frozen_fence_days,
    reschedule_tolerance_days: row.reschedule_tolerance_days,
    planner_code: row.planner_code,
    status: row.status,
    expected_updated_at: row.updated_at,
    ...patch,
  };
}

// ---- the list ------------------------------------------------------------------

export function PlanningParametersScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const offline = useOffline();
  const { facility, facilities } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canEdit = perms.has("planning.demand.manage");
  const refresh = usePlanningRefresh();

  const [filters, setFilters] = useState<ParameterFilters>({ status: "active" });
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const list = usePlanningParameters(facility.id, filters, cursors[page]);
  const rows = list.data?.items ?? [];
  const products = useProductIndex(rows.map((r) => r.product_id));
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [drawer, setDrawer] = useState<
    | { product: Product | undefined; row: PlanningParametersRow | null; productId: string }
    | undefined
  >(undefined);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    setCursors([undefined]);
    setSelected(new Set());
  }, [facility.id, filters]);

  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const filtered =
    filters.status !== "active" ||
    filters.replenishment_type !== undefined ||
    filters.mps_relevant !== undefined;

  const openProduct = async (product: Product) => {
    setOpening(true);
    try {
      const row = await readParametersOf(facility.id, product.id);
      setDrawer({ product, row, productId: product.id });
    } catch (error) {
      if (error instanceof UnauthorizedError) void navigate({ to: "/login" });
      else setDrawer({ product, row: null, productId: product.id });
    } finally {
      setOpening(false);
    }
  };

  const columns: DataTableColumn<PlanningParametersRow>[] = [
    {
      key: "product",
      header: t("planning.parameters.columns.product"),
      id: true,
      render: (row) => (
        <ProductCell product={products.byId.get(row.product_id)} id={row.product_id} />
      ),
    },
    {
      key: "replenishment",
      header: t("planning.parameters.columns.replenishment"),
      render: (row) => <ReplenishmentChip row={row} facilities={facilities} t={t} />,
    },
    {
      key: "mps",
      header: t("planning.parameters.columns.mps"),
      render: (row) => (row.mps_relevant ? t("planning.common.yes") : t("planning.common.no")),
    },
    {
      key: "lead",
      header: t("planning.parameters.columns.leadTime"),
      numeric: true,
      render: (row) => t("planning.common.daysCount", { count: row.lead_time_days }),
    },
    {
      key: "lot",
      header: t("planning.parameters.columns.lotSizing"),
      render: (row) =>
        row.lot_sizing_method === "lot_for_lot"
          ? t("planning.parameters.lot.lot_for_lot")
          : t("planning.parameters.lot.minMultipleShort", {
              min: formatGridQty(row.min_lot_qty, i18n.language),
              multiple:
                row.lot_multiple_qty !== null
                  ? formatGridQty(row.lot_multiple_qty, i18n.language)
                  : "-",
            }),
    },
    {
      key: "fence",
      header: t("planning.parameters.columns.fence"),
      numeric: true,
      render: (row) => t("planning.common.daysCount", { count: row.frozen_fence_days }),
    },
    {
      key: "tolerance",
      header: t("planning.parameters.columns.tolerance"),
      numeric: true,
      render: (row) => t("planning.common.daysCount", { count: row.reschedule_tolerance_days }),
    },
    {
      key: "planner",
      header: t("planning.parameters.columns.plannerCode"),
      render: (row) => row.planner_code ?? "",
    },
    {
      key: "safety",
      header: t("planning.parameters.columns.safetyStock"),
      render: (row) => safetyLabel(row.safety_stock_policy, i18n.language, t),
    },
    {
      key: "status",
      header: t("planning.parameters.columns.status"),
      render: (row) => (
        <StatusChip variant={row.status === "active" ? "ok" : "neutral"}>
          {t(`planning.status.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "actions",
      header: t("planning.parameters.columns.actions"),
      actions: true,
      render: (row) => (
        <Button
          size="sm"
          data-testid="planning-parameters-open"
          onClick={(e) => {
            e.stopPropagation();
            setDrawer({
              product: products.byId.get(row.product_id),
              row,
              productId: row.product_id,
            });
          }}
        >
          {canEdit ? t("planning.common.edit") : t("planning.common.view")}
        </Button>
      ),
    },
  ];

  const empty = filtered ? (
    <EmptyState
      title={t("planning.parameters.filteredEmptyTitle")}
      body={t("planning.parameters.filteredEmptyBody", { filters: describeFilters(filters, t) })}
      ctaLabel={t("planning.common.clearFilters")}
      onCta={() => {
        setFilters({ status: "active" });
      }}
    />
  ) : (
    <EmptyState
      title={t("planning.parameters.emptyTitle")}
      body={t("planning.parameters.emptyBody")}
    />
  );

  const bulkRows = rows.filter((r) => selected.has(r.id));

  return (
    <section className="planning-screen" data-testid="planning-parameters">
      <PlanningHeader title={t("planning.parameters.title")} />
      {canEdit && offline ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
      {!canEdit ? <p className="planning-note">{t("planning.parameters.readOnly")}</p> : null}
      {canEdit ? (
        <ProductPicker
          label={t("planning.parameters.addProduct")}
          disabled={offline || opening}
          onPick={(p) => void openProduct(p)}
          data-testid="planning-parameters-add"
        />
      ) : null}
      <div className="filter-bar">
        <Field label={t("planning.parameters.filters.status")}>
          <Select
            value={filters.status}
            data-testid="planning-parameters-status"
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value as ParameterFilters["status"] });
            }}
          >
            <option value="active">{t("planning.status.active")}</option>
            <option value="archived">{t("planning.status.archived")}</option>
            <option value="all">{t("planning.common.all")}</option>
          </Select>
        </Field>
        <Field label={t("planning.parameters.filters.replenishment")}>
          <Select
            value={filters.replenishment_type ?? ""}
            data-testid="planning-parameters-replenishment"
            onChange={(e) => {
              const v = e.target.value;
              setFilters({
                ...filters,
                replenishment_type: v === "" ? undefined : (v as "make" | "buy" | "transfer"),
              });
            }}
          >
            <option value="">{t("planning.common.all")}</option>
            <option value="make">{t("planning.parameters.replenishment.make")}</option>
            <option value="buy">{t("planning.parameters.replenishment.buy")}</option>
            <option value="transfer">{t("planning.parameters.replenishment.transfer")}</option>
          </Select>
        </Field>
        <Field label={t("planning.parameters.filters.mps")}>
          <Select
            value={filters.mps_relevant ?? ""}
            data-testid="planning-parameters-mps"
            onChange={(e) => {
              const v = e.target.value;
              setFilters({
                ...filters,
                mps_relevant: v === "" ? undefined : (v as "true" | "false"),
              });
            }}
          >
            <option value="">{t("planning.common.all")}</option>
            <option value="true">{t("planning.common.yes")}</option>
            <option value="false">{t("planning.common.no")}</option>
          </Select>
        </Field>
      </div>
      {list.error !== null && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("planning.parameters.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowTestId="planning-parameters-row"
          // Eleven columns plus the selection cell held the Russian list wider
          // than 1440 px with Edit off the glass; `fit` lets two-word headers
          // and chips wrap, as the customer book does (D-336 ruling 1).
          fit
          loading={list.isPending}
          loadingLabel={t("planning.parameters.loading")}
          empty={empty}
          caption={t("planning.parameters.title")}
          onRowClick={(row) => {
            setDrawer({
              product: products.byId.get(row.product_id),
              row,
              productId: row.product_id,
            });
          }}
          {...(canEdit && desktop && !offline
            ? {
                selectable: {
                  selected,
                  onChange: setSelected,
                  headerLabel: t("planning.bulk.selectAll"),
                  rowLabel: (row: PlanningParametersRow) =>
                    t("planning.bulk.selectRow", {
                      product: products.byId.get(row.product_id)?.sku ?? row.product_id,
                    }),
                  testId: "planning-parameters-select",
                },
                bulkBar: {
                  countLabel: t("planning.bulk.count", { count: selected.size }),
                  clearLabel: t("planning.bulk.clear"),
                  onClear: () => {
                    setSelected(new Set());
                  },
                  children: (
                    <BulkApply
                      facilityId={facility.id}
                      rows={bulkRows}
                      productLabel={(row) =>
                        products.byId.get(row.product_id)?.sku ?? row.product_id
                      }
                      onDone={() => void refresh.parameters(facility.id)}
                    />
                  ),
                },
              }
            : {})}
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && list.data?.next_cursor)
                setCursors([...cursors, list.data.next_cursor]);
              else if (n < page + 1) setCursors(cursors.slice(0, -1));
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (list.data?.next_cursor ?? null) !== null,
          }}
        />
      )}
      {drawer !== undefined ? (
        <ParametersDrawer
          key={drawer.productId}
          facility={facility}
          facilities={facilities}
          product={drawer.product}
          productId={drawer.productId}
          initial={drawer.row}
          canEdit={canEdit}
          offline={offline}
          onClose={() => {
            setDrawer(undefined);
          }}
          onSaved={() => void refresh.parameters(facility.id)}
        />
      ) : null}
    </section>
  );
}

function describeFilters(filters: ParameterFilters, t: TFunction): string {
  const parts: string[] = [];
  if (filters.status !== "active")
    parts.push(
      t(filters.status === "all" ? "planning.common.all" : `planning.status.${filters.status}`),
    );
  if (filters.replenishment_type !== undefined)
    parts.push(t(`planning.parameters.replenishment.${filters.replenishment_type}`));
  if (filters.mps_relevant !== undefined) {
    parts.push(
      t(
        filters.mps_relevant === "true"
          ? "planning.parameters.mpsYes"
          : "planning.parameters.mpsNo",
      ),
    );
  }
  return parts.join(", ");
}

function ProductCell({ product, id }: { product: Product | undefined; id: string }) {
  if (product === undefined) return <code className="planning-sku">{shortId(id)}</code>;
  return (
    <span className="planning-product">
      <span className="planning-sku">{product.sku}</span>
      <span className="planning-product-name">{product.name}</span>
    </span>
  );
}

function ReplenishmentChip({
  row,
  facilities,
  t,
}: {
  row: PlanningParameters;
  facilities: readonly Facility[];
  t: TFunction;
}) {
  const label = t(`planning.parameters.replenishment.${row.replenishment_type}`);
  if (row.replenishment_type !== "transfer")
    return <StatusChip variant="neutral">{label}</StatusChip>;
  const source =
    facilities.find((f) => f.id === row.source_facility_id)?.name ??
    t("planning.parameters.otherFacility");
  return (
    <StatusChip variant="accent">
      {t("planning.parameters.transferFrom", { facility: source })}
    </StatusChip>
  );
}

function safetyLabel(
  policy: SafetyStockPolicy | null | undefined,
  locale: string,
  t: TFunction,
): string {
  if (policy?.status !== "active") return t("planning.safety.none");
  return policy.method === "static_qty"
    ? t("planning.safety.staticShort", { qty: formatGridQty(policy.static_qty, locale) })
    : t("planning.safety.daysShort", { count: policy.days_of_cover ?? 0 });
}

// ---- the drawer ----------------------------------------------------------------

interface ParamForm {
  replenishment_type: "make" | "buy" | "transfer";
  source_facility_id: string;
  mps_relevant: boolean;
  lead_time_days: string;
  lot_sizing_method: "lot_for_lot" | "min_multiple";
  min_lot_qty: string;
  lot_multiple_qty: string;
  frozen_fence_days: string;
  reschedule_tolerance_days: string;
  planner_code: string;
}

function paramFormOf(row: PlanningParameters | null, locale: string): ParamForm {
  if (row === null) {
    return {
      replenishment_type: "make",
      source_facility_id: "",
      mps_relevant: false,
      lead_time_days: "0",
      lot_sizing_method: "lot_for_lot",
      min_lot_qty: "",
      lot_multiple_qty: "",
      frozen_fence_days: "7",
      reschedule_tolerance_days: "2",
      planner_code: "",
    };
  }
  return {
    replenishment_type: row.replenishment_type,
    source_facility_id: row.source_facility_id ?? "",
    mps_relevant: row.mps_relevant,
    lead_time_days: String(row.lead_time_days),
    lot_sizing_method: row.lot_sizing_method,
    min_lot_qty: row.min_lot_qty !== null ? editGridQty(row.min_lot_qty, locale) : "",
    lot_multiple_qty:
      row.lot_multiple_qty !== null ? editGridQty(row.lot_multiple_qty, locale) : "",
    frozen_fence_days: String(row.frozen_fence_days),
    reschedule_tolerance_days: String(row.reschedule_tolerance_days),
    planner_code: row.planner_code ?? "",
  };
}

type ParamField = keyof ParamForm;
type ParamErrors = Partial<Record<ParamField | "form", string>>;

/** Builds the PUT body from the drawer's form, or the field problems that stop it. */
export function paramBodyFromForm(
  form: ParamForm,
  status: "active" | "archived",
  expected: string | null,
  locale: string,
): { body: PlanningParametersPut } | { errors: ParamErrors } {
  const errors: ParamErrors = {};
  const whole = (field: ParamField, value: string) => {
    const n = parseWhole(value, 0, 2147483647);
    if (n === undefined) errors[field] = "planning.parameters.errors.wholeDays";
    return n ?? 0;
  };
  const qty = (field: ParamField, value: string): string | null => {
    if (value.trim() === "") return null;
    const parsed = parseGridQty(value, locale);
    if (!parsed.ok || Number(parsed.value) <= 0) {
      errors[field] = "planning.parameters.errors.positiveQty";
      return null;
    }
    return parsed.value;
  };
  const planner = form.planner_code.trim();
  if (planner.length > 64) errors.planner_code = "planning.parameters.errors.plannerCode";
  const minMultiple = form.lot_sizing_method === "min_multiple";
  const body: PlanningParametersPut = {
    replenishment_type: form.replenishment_type,
    source_facility_id:
      form.replenishment_type === "transfer" && form.source_facility_id !== ""
        ? form.source_facility_id
        : null,
    mps_relevant: form.mps_relevant,
    lead_time_days: whole("lead_time_days", form.lead_time_days),
    lot_sizing_method: form.lot_sizing_method,
    min_lot_qty: minMultiple ? qty("min_lot_qty", form.min_lot_qty) : null,
    lot_multiple_qty: minMultiple ? qty("lot_multiple_qty", form.lot_multiple_qty) : null,
    frozen_fence_days: whole("frozen_fence_days", form.frozen_fence_days),
    reschedule_tolerance_days: whole("reschedule_tolerance_days", form.reschedule_tolerance_days),
    planner_code: planner === "" ? null : planner,
    status,
    expected_updated_at: expected,
  };
  return Object.keys(errors).length > 0 ? { errors } : { body };
}

/** Field errors for the reasons of a 422 `PLANNING_PARAMETERS_INVALID`. */
export function paramErrorsOfReasons(reasons: readonly string[]): ParamErrors {
  const errors: ParamErrors = {};
  for (const reason of reasons) {
    if (reason === "METHOD_FIELDS_MISMATCH")
      errors.min_lot_qty = "planning.parameters.reasons.METHOD_FIELDS_MISMATCH";
    else if (
      reason === "TRANSFER_SOURCE_REQUIRED" ||
      reason === "SOURCE_NOT_ALLOWED" ||
      reason === "SOURCE_FACILITY_INVALID"
    ) {
      errors.source_facility_id = `planning.parameters.reasons.${reason}`;
    }
  }
  return errors;
}

interface SafetyForm {
  method: "static_qty" | "days_of_cover";
  static_qty: string;
  days_of_cover: string;
  valid_from: string;
  valid_to: string;
}

function safetyFormOf(policy: SafetyStockPolicy | null | undefined, locale: string): SafetyForm {
  if (policy?.status !== "active") {
    return {
      method: "static_qty",
      static_qty: "",
      days_of_cover: "",
      valid_from: "",
      valid_to: "",
    };
  }
  return {
    method: policy.method,
    static_qty: policy.static_qty !== null ? editGridQty(policy.static_qty, locale) : "",
    days_of_cover: policy.days_of_cover !== null ? String(policy.days_of_cover) : "",
    valid_from: policy.valid_from,
    valid_to: policy.valid_to ?? "",
  };
}

type SafetyErrors = Partial<Record<keyof SafetyForm | "form", string>>;

function ParametersDrawer(props: {
  facility: Facility;
  facilities: readonly Facility[];
  product: Product | undefined;
  productId: string;
  initial: PlanningParametersRow | null;
  canEdit: boolean;
  offline: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language;
  const [row, setRow] = useState<PlanningParameters | null>(props.initial);
  const [policy, setPolicy] = useState<SafetyStockPolicy | null>(
    props.initial?.safety_stock_policy?.status === "active"
      ? props.initial.safety_stock_policy
      : null,
  );
  const [form, setForm] = useState<ParamForm>(() => paramFormOf(props.initial, locale));
  const [errors, setErrors] = useState<ParamErrors>({});
  const [paramNotice, setParamNotice] = useState<string | undefined>(undefined);
  const [safety, setSafety] = useState<SafetyForm>(() => safetyFormOf(policy, locale));
  const [safetyErrors, setSafetyErrors] = useState<SafetyErrors>({});
  const [safetyNotice, setSafetyNotice] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<"params" | "safety" | "archive" | undefined>(undefined);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const editable = props.canEdit && !props.offline && pending === undefined;
  const archived = row?.status === "archived";
  const otherFacilities = props.facilities.filter((f) => f.id !== props.facility.id);
  const title =
    props.product !== undefined
      ? `${props.product.sku} · ${props.product.name}`
      : shortId(props.productId);

  const handleAuth = (error: unknown): boolean => {
    if (error instanceof UnauthorizedError) {
      void navigate({ to: "/login" });
      return true;
    }
    return false;
  };

  const saveParams = async (status: "active" | "archived") => {
    const built = paramBodyFromForm(form, status, row?.updated_at ?? null, locale);
    if ("errors" in built) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    setParamNotice(undefined);
    setPending(status === "archived" ? "archive" : "params");
    try {
      const saved = await putPlanningParameters(props.facility.id, props.productId, built.body);
      setRow(saved);
      setForm(paramFormOf(saved, locale));
      if (status === "archived") {
        setPolicy(null);
        setSafety(safetyFormOf(null, locale));
      }
      setParamNotice(
        status === "archived"
          ? "planning.parameters.archivedNotice"
          : row?.status === "archived"
            ? "planning.parameters.restoredNotice"
            : "planning.parameters.savedNotice",
      );
      props.onSaved();
    } catch (error) {
      if (handleAuth(error)) return;
      const code = problemCode(error);
      if (code === "PLANNING_PARAMETERS_INVALID") {
        setErrors(
          paramErrorsOfReasons((problemExtra(error, "reasons") as string[] | undefined) ?? []),
        );
      } else if (code === "PLANNING_PARAMETERS_CHANGED") {
        const current = problemExtra(error, "current") as PlanningParameters | undefined;
        if (current !== undefined) {
          setRow(current);
          setForm(paramFormOf(current, locale));
        }
        setParamNotice("planning.common.changedBySomeone");
      } else if (code === "PRODUCT_ARCHIVED" || code === "PRODUCT_NOT_FOUND") {
        setErrors({ form: `planning.parameters.problems.${code}` });
      } else {
        setErrors({ form: "planning.common.saveFailedShort" });
      }
    } finally {
      setPending(undefined);
      setConfirmArchive(false);
    }
  };

  const saveSafety = async (archive: boolean) => {
    const errs: SafetyErrors = {};
    let body: SafetyStockPut;
    if (archive && policy !== null) {
      body = {
        method: policy.method,
        static_qty: policy.static_qty,
        days_of_cover: policy.days_of_cover,
        valid_from: policy.valid_from,
        valid_to: policy.valid_to,
        status: "archived",
        expected_updated_at: policy.updated_at,
      };
    } else {
      let staticQty: string | null = null;
      let days: number | null = null;
      if (safety.method === "static_qty") {
        const parsed = parseGridQty(safety.static_qty, locale);
        if (!parsed.ok) errs.static_qty = "planning.safety.errors.qty";
        else staticQty = parsed.value;
      } else {
        const n = parseWhole(safety.days_of_cover, 1, 2147483647);
        if (n === undefined) errs.days_of_cover = "planning.safety.errors.days";
        else days = n;
      }
      if (Object.keys(errs).length > 0) {
        setSafetyErrors(errs);
        return;
      }
      body = {
        method: safety.method,
        static_qty: staticQty,
        days_of_cover: days,
        ...(safety.valid_from !== "" ? { valid_from: safety.valid_from } : {}),
        valid_to: safety.valid_to === "" ? null : safety.valid_to,
        status: "active",
        expected_updated_at: policy?.updated_at ?? null,
      };
    }
    setSafetyErrors({});
    setSafetyNotice(undefined);
    setPending("safety");
    try {
      const saved = await putSafetyStock(props.facility.id, props.productId, body);
      const next = saved !== null && saved.status === "active" ? saved : null;
      setPolicy(next);
      setSafety(safetyFormOf(next, locale));
      setSafetyNotice(archive ? "planning.safety.removedNotice" : "planning.safety.savedNotice");
      props.onSaved();
    } catch (error) {
      if (handleAuth(error)) return;
      const code = problemCode(error);
      if (code === "SAFETY_STOCK_INVALID") {
        const next: SafetyErrors = {};
        for (const reason of (problemExtra(error, "reasons") as string[] | undefined) ?? []) {
          if (reason === "METHOD_FIELDS_MISMATCH")
            next[safety.method] = "planning.safety.reasons.METHOD_FIELDS_MISMATCH";
          else if (reason === "VALID_RANGE_INVALID")
            next.valid_to = "planning.safety.reasons.VALID_RANGE_INVALID";
          else if (reason === "NO_PLANNING_PARAMETERS")
            next.form = "planning.safety.reasons.NO_PLANNING_PARAMETERS";
        }
        setSafetyErrors(next);
      } else if (code === "SAFETY_STOCK_CHANGED" || code === "SAFETY_STOCK_NOT_FOUND") {
        const current =
          (problemExtra(error, "current") as SafetyStockPolicy | null | undefined) ?? null;
        setPolicy(current);
        setSafety(safetyFormOf(current, locale));
        setSafetyNotice("planning.common.changedBySomeone");
      } else if (code === "PRODUCT_ARCHIVED") {
        setSafetyErrors({ form: "planning.parameters.problems.PRODUCT_ARCHIVED" });
      } else if (error instanceof RequestError && error.httpStatus === 400) {
        setSafetyErrors({ form: "planning.safety.errors.invalid" });
      } else {
        setSafetyErrors({ form: "planning.common.saveFailedShort" });
      }
    } finally {
      setPending(undefined);
    }
  };

  const fieldError = (key: string | undefined, field: string) =>
    key !== undefined ? (
      <span
        className="field-error"
        role="alert"
        data-testid="planning-field-error"
        data-field={field}
      >
        {t(key)}
      </span>
    ) : null;

  const setF = <K extends ParamField>(name: K, value: ParamForm[K]) => {
    setForm({ ...form, [name]: value });
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const textField = (
    name: ParamField,
    label: string,
    inputMode: "numeric" | "decimal" | "text" = "numeric",
  ) => (
    <div className="planning-field">
      <Field label={label}>
        <input
          type="text"
          inputMode={inputMode}
          value={form[name] as string}
          disabled={!editable || archived}
          aria-invalid={errors[name] !== undefined || undefined}
          data-testid={`planning-param-${name}`}
          onChange={(e) => {
            setF(name, e.target.value as never);
          }}
        />
      </Field>
      {fieldError(errors[name], name)}
    </div>
  );

  const safetyAllowed = row !== null && row.status === "active";

  return (
    <Modal
      label={t("planning.parameters.drawerLabel", { product: title })}
      heading={title}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      pending={pending !== undefined}
      className="planning-drawer"
      testId="planning-parameters-drawer"
    >
      <section className="planning-drawer-section" aria-labelledby="planning-params-h">
        <h2 id="planning-params-h">{t("planning.parameters.sectionTitle")}</h2>
        {archived ? <p className="planning-note">{t("planning.parameters.archivedRow")}</p> : null}
        {paramNotice !== undefined ? (
          <Banner
            kind={paramNotice === "planning.common.changedBySomeone" ? "warn" : "ok"}
            data-testid="planning-param-notice"
          >
            {t(paramNotice)}
          </Banner>
        ) : null}
        {errors.form !== undefined ? (
          <Banner kind="bad" data-testid="planning-param-problem">
            {t(errors.form)}
          </Banner>
        ) : null}
        <div className="planning-form">
          <div className="planning-field">
            <Field label={t("planning.parameters.fields.replenishment")}>
              <Select
                value={form.replenishment_type}
                disabled={!editable || archived}
                data-testid="planning-param-replenishment_type"
                onChange={(e) => {
                  setF("replenishment_type", e.target.value as ParamForm["replenishment_type"]);
                }}
              >
                <option value="make">{t("planning.parameters.replenishment.make")}</option>
                <option value="buy">{t("planning.parameters.replenishment.buy")}</option>
                <option value="transfer">{t("planning.parameters.replenishment.transfer")}</option>
              </Select>
            </Field>
          </div>
          {form.replenishment_type === "transfer" ? (
            <div className="planning-field">
              <Field label={t("planning.parameters.fields.source")}>
                <Select
                  value={form.source_facility_id}
                  disabled={!editable || archived}
                  aria-invalid={errors.source_facility_id !== undefined || undefined}
                  data-testid="planning-param-source_facility_id"
                  onChange={(e) => {
                    setF("source_facility_id", e.target.value);
                  }}
                >
                  <option value="">{t("planning.parameters.chooseSource")}</option>
                  {otherFacilities.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {fieldError(errors.source_facility_id, "source_facility_id")}
            </div>
          ) : null}
          <div className="planning-field">
            <Field label={t("planning.parameters.fields.mps")}>
              <Select
                value={form.mps_relevant ? "true" : "false"}
                disabled={!editable || archived}
                data-testid="planning-param-mps_relevant"
                onChange={(e) => {
                  setF("mps_relevant", e.target.value === "true");
                }}
              >
                <option value="true">{t("planning.common.yes")}</option>
                <option value="false">{t("planning.common.no")}</option>
              </Select>
            </Field>
          </div>
          {textField("lead_time_days", t("planning.parameters.fields.leadTime"))}
          <div className="planning-field">
            <Field label={t("planning.parameters.fields.lotSizing")}>
              <Select
                value={form.lot_sizing_method}
                disabled={!editable || archived}
                data-testid="planning-param-lot_sizing_method"
                onChange={(e) => {
                  setF("lot_sizing_method", e.target.value as ParamForm["lot_sizing_method"]);
                }}
              >
                <option value="lot_for_lot">{t("planning.parameters.lot.lot_for_lot")}</option>
                <option value="min_multiple">{t("planning.parameters.lot.min_multiple")}</option>
              </Select>
            </Field>
          </div>
          {form.lot_sizing_method === "min_multiple" ? (
            <>
              {textField("min_lot_qty", t("planning.parameters.fields.minLot"), "decimal")}
              {textField("lot_multiple_qty", t("planning.parameters.fields.multiple"), "decimal")}
            </>
          ) : null}
          {textField("frozen_fence_days", t("planning.parameters.fields.fence"))}
          {textField("reschedule_tolerance_days", t("planning.parameters.fields.tolerance"))}
          {textField("planner_code", t("planning.parameters.fields.plannerCode"), "text")}
        </div>
        {props.canEdit ? (
          <div className="form-actions">
            {archived ? (
              <Button
                variant="primary"
                disabled={!editable}
                busy={pending === "params"}
                busyLabel={t("planning.common.saving")}
                data-testid="planning-param-restore"
                onClick={() => void saveParams("active")}
              >
                {t("planning.parameters.restore")}
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  disabled={!editable}
                  busy={pending === "params"}
                  busyLabel={t("planning.common.saving")}
                  data-testid="planning-param-save"
                  onClick={() => void saveParams("active")}
                >
                  {t("planning.parameters.save")}
                </Button>
                {row !== null ? (
                  <Button
                    variant="danger"
                    disabled={!editable}
                    data-testid="planning-param-archive"
                    onClick={() => {
                      setConfirmArchive(true);
                    }}
                  >
                    {t("planning.parameters.archive")}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        ) : null}
        {confirmArchive ? (
          <div
            className="planning-confirm"
            role="alertdialog"
            aria-label={t("planning.parameters.archiveConfirmTitle")}
          >
            <p>
              {policy !== null
                ? t("planning.parameters.archiveConfirmPolicy", {
                    policy: safetyLabel(policy, locale, t),
                  })
                : t("planning.parameters.archiveConfirm")}
            </p>
            <div className="form-actions">
              <Button
                variant="danger"
                busy={pending === "archive"}
                busyLabel={t("planning.common.saving")}
                data-testid="planning-param-archive-confirm"
                onClick={() => void saveParams("archived")}
              >
                {t("planning.parameters.archive")}
              </Button>
              <Button
                onClick={() => {
                  setConfirmArchive(false);
                }}
              >
                {t("planning.common.cancel")}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section
        className="planning-drawer-section"
        aria-labelledby="planning-safety-h"
        data-testid="planning-safety"
      >
        <h2 id="planning-safety-h">{t("planning.safety.title")}</h2>
        {!safetyAllowed ? (
          <p className="planning-note">{t("planning.safety.needsParameters")}</p>
        ) : null}
        {safetyNotice !== undefined ? (
          <Banner
            kind={safetyNotice === "planning.common.changedBySomeone" ? "warn" : "ok"}
            data-testid="planning-safety-notice"
          >
            {t(safetyNotice)}
          </Banner>
        ) : null}
        {safetyErrors.form !== undefined ? (
          <Banner kind="bad" data-testid="planning-safety-problem">
            {t(safetyErrors.form)}
          </Banner>
        ) : null}
        <p className="planning-hint" data-testid="planning-safety-current">
          {t("planning.safety.current", { policy: safetyLabel(policy, locale, t) })}
        </p>
        <div className="planning-form">
          <div className="planning-field">
            <Field label={t("planning.safety.method")}>
              <Select
                value={safety.method}
                disabled={!editable || !safetyAllowed}
                data-testid="planning-safety-method"
                onChange={(e) => {
                  setSafety({ ...safety, method: e.target.value as SafetyForm["method"] });
                  setSafetyErrors({});
                }}
              >
                <option value="static_qty">{t("planning.safety.methods.static_qty")}</option>
                <option value="days_of_cover">{t("planning.safety.methods.days_of_cover")}</option>
              </Select>
            </Field>
          </div>
          {safety.method === "static_qty" ? (
            <div className="planning-field">
              <Field label={t("planning.safety.staticQty")}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={safety.static_qty}
                  disabled={!editable || !safetyAllowed}
                  aria-invalid={safetyErrors.static_qty !== undefined || undefined}
                  data-testid="planning-safety-static_qty"
                  onChange={(e) => {
                    setSafety({ ...safety, static_qty: e.target.value });
                  }}
                />
              </Field>
              {fieldError(safetyErrors.static_qty, "static_qty")}
            </div>
          ) : (
            <div className="planning-field">
              <Field label={t("planning.safety.daysOfCover")}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={safety.days_of_cover}
                  disabled={!editable || !safetyAllowed}
                  aria-invalid={safetyErrors.days_of_cover !== undefined || undefined}
                  data-testid="planning-safety-days_of_cover"
                  onChange={(e) => {
                    setSafety({ ...safety, days_of_cover: e.target.value });
                  }}
                />
              </Field>
              {fieldError(safetyErrors.days_of_cover, "days_of_cover")}
            </div>
          )}
          <div className="planning-field">
            <Field label={t("planning.safety.validFrom")}>
              <input
                type="date"
                value={safety.valid_from}
                disabled={!editable || !safetyAllowed}
                data-testid="planning-safety-valid_from"
                onChange={(e) => {
                  setSafety({ ...safety, valid_from: e.target.value });
                }}
              />
            </Field>
            <span className="planning-hint">{t("planning.safety.validFromHint")}</span>
          </div>
          <div className="planning-field">
            <Field label={t("planning.safety.validTo")}>
              <input
                type="date"
                value={safety.valid_to}
                disabled={!editable || !safetyAllowed}
                aria-invalid={safetyErrors.valid_to !== undefined || undefined}
                data-testid="planning-safety-valid_to"
                onChange={(e) => {
                  setSafety({ ...safety, valid_to: e.target.value });
                }}
              />
            </Field>
            {fieldError(safetyErrors.valid_to, "valid_to")}
          </div>
        </div>
        {props.canEdit ? (
          <div className="form-actions">
            <Button
              variant="primary"
              disabled={!editable || !safetyAllowed}
              busy={pending === "safety"}
              busyLabel={t("planning.common.saving")}
              data-testid="planning-safety-save"
              onClick={() => void saveSafety(false)}
            >
              {t("planning.safety.save")}
            </Button>
            {policy !== null ? (
              <Button
                variant="danger"
                disabled={!editable}
                data-testid="planning-safety-remove"
                onClick={() => void saveSafety(true)}
              >
                {t("planning.safety.remove")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>
    </Modal>
  );
}

// ---- the selection bar -------------------------------------------------------------

type BulkField =
  | "mps_relevant"
  | "planner_code"
  | "lead_time_days"
  | "frozen_fence_days"
  | "reschedule_tolerance_days";

interface BulkOutcome {
  readonly label: string;
  readonly result: "saved" | "changed" | "refused" | "failed";
  readonly reason?: string;
}

/** Counts per outcome, a refusal counted per reason (D-323 ruling 7). */
export function summariseBulk(outcomes: readonly BulkOutcome[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const o of outcomes) {
    const key = o.result === "refused" ? `refused:${o.reason ?? ""}` : o.result;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function BulkApply(props: {
  facilityId: string;
  rows: readonly PlanningParametersRow[];
  productLabel: (row: PlanningParametersRow) => string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [field, setField] = useState<BulkField>("planner_code");
  const [value, setValue] = useState("");
  const [running, setRunning] = useState(false);
  const [outcomes, setOutcomes] = useState<readonly BulkOutcome[]>([]);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const patch = useMemo((): Partial<PlanningParametersPut> | undefined => {
    if (field === "mps_relevant") return { mps_relevant: value === "true" };
    if (field === "planner_code") {
      const v = value.trim();
      return v.length > 64 ? undefined : { planner_code: v === "" ? null : v };
    }
    const n = parseWhole(value, 0, 2147483647);
    return n === undefined ? undefined : { [field]: n };
  }, [field, value]);

  const run = async () => {
    if (patch === undefined) {
      setProblem(
        field === "planner_code"
          ? "planning.parameters.errors.plannerCode"
          : "planning.parameters.errors.wholeDays",
      );
      return;
    }
    setProblem(undefined);
    setRunning(true);
    const results: BulkOutcome[] = [];
    for (const row of props.rows) {
      const label = props.productLabel(row);
      try {
        await putPlanningParameters(props.facilityId, row.product_id, parametersBody(row, patch));
        results.push({ label, result: "saved" });
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          setRunning(false);
          return;
        }
        const code = problemCode(error);
        if (code === "PLANNING_PARAMETERS_CHANGED") results.push({ label, result: "changed" });
        else if (code === "PLANNING_PARAMETERS_INVALID") {
          const reasons = (problemExtra(error, "reasons") as string[] | undefined) ?? [];
          results.push({ label, result: "refused", reason: reasons[0] ?? code });
        } else if (code !== undefined && error instanceof RequestError && error.httpStatus < 500) {
          results.push({ label, result: "refused", reason: code });
        } else results.push({ label, result: "failed" });
      }
    }
    setOutcomes(results);
    setRunning(false);
    props.onDone();
  };

  const summary = summariseBulk(outcomes);
  return (
    <div className="planning-bulk" data-testid="planning-bulk">
      <Field label={t("planning.bulk.field")}>
        <Select
          value={field}
          data-testid="planning-bulk-field"
          onChange={(e) => {
            setField(e.target.value as BulkField);
            setValue(e.target.value === "mps_relevant" ? "true" : "");
          }}
        >
          <option value="planner_code">{t("planning.parameters.fields.plannerCode")}</option>
          <option value="mps_relevant">{t("planning.parameters.fields.mps")}</option>
          <option value="lead_time_days">{t("planning.parameters.fields.leadTime")}</option>
          <option value="frozen_fence_days">{t("planning.parameters.fields.fence")}</option>
          <option value="reschedule_tolerance_days">
            {t("planning.parameters.fields.tolerance")}
          </option>
        </Select>
      </Field>
      <Field label={t("planning.bulk.value")}>
        {field === "mps_relevant" ? (
          <Select
            value={value}
            data-testid="planning-bulk-value"
            onChange={(e) => {
              setValue(e.target.value);
            }}
          >
            <option value="true">{t("planning.common.yes")}</option>
            <option value="false">{t("planning.common.no")}</option>
          </Select>
        ) : (
          <input
            type="text"
            value={value}
            data-testid="planning-bulk-value"
            onChange={(e) => {
              setValue(e.target.value);
            }}
          />
        )}
      </Field>
      <Button
        variant="primary"
        size="sm"
        busy={running}
        busyLabel={t("planning.bulk.applying")}
        data-testid="planning-bulk-apply"
        onClick={() => void run()}
      >
        {t("planning.bulk.apply", { count: props.rows.length })}
      </Button>
      {problem !== undefined ? (
        <span className="field-error" role="alert">
          {t(problem)}
        </span>
      ) : null}
      {outcomes.length > 0 ? (
        <div className="planning-bulk-outcome" role="status" data-testid="planning-bulk-outcome">
          <p>
            {[...summary.entries()]
              .map(([key, count]) =>
                key.startsWith("refused:")
                  ? t("planning.bulk.refused", {
                      count,
                      reason: enumLabel(t, "planning.parameters.reasons", key.slice(8)),
                    })
                  : t(`planning.bulk.${key}`, { count }),
              )
              .join(" · ")}
          </p>
          <ul>
            {outcomes
              .filter((o) => o.result !== "saved")
              .map((o) => (
                <li key={o.label}>
                  {o.label}:{" "}
                  {o.result === "refused"
                    ? t(`planning.parameters.reasons.${o.reason ?? ""}`, {
                        defaultValue: o.reason ?? "",
                      })
                    : t(`planning.bulk.row.${o.result}`)}
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
